import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigation, useRoute } from '@react-navigation/native'
import React from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components/native'

import { getProfileHookConfig } from 'features/navigation/navigators/ProfileStackNavigator/getProfileHookConfig'
import { UseNavigationType, UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { newEmailSelectionSchema } from 'features/profile/pages/NewEmailSelection/schema/newEmailSelectionSchema'
import { useNewEmailSelectionMutation } from 'features/profile/queries/useNewEmailSelectionMutation'
import { eventMonitoring } from 'libs/monitoring/services'
import { EmailInputController } from 'shared/forms/controllers/EmailInputController'
import { Form } from 'ui/components/Form'
import { SUGGESTION_DELAY_IN_MS } from 'ui/components/inputs/EmailInputWithSpellingHelp/useEmailSpellingHelp'
import { Banner } from 'ui/designSystem/Banner/Banner'
import { Button } from 'ui/designSystem/Button/Button'
import { showErrorSnackBar, showSuccessSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { PageWithHeader } from 'ui/pages/PageWithHeader'

type FormValues = {
  newEmail: string
}

export const NewEmailSelection = () => {
  const { replace } = useNavigation<UseNavigationType>()
  const { params } = useRoute<UseRouteType<'NewEmailSelection'>>()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      newEmail: '',
    },
    resolver: yupResolver(newEmailSelectionSchema),
    delayError: SUGGESTION_DELAY_IN_MS,
    mode: 'all',
  })

  const { mutate: selectNewEmail, isPending } = useNewEmailSelectionMutation({
    onSuccess: () => {
      showSuccessSnackBar(
        'E-mail envoyé sur ta nouvelle adresse\u00a0! Tu as 24h pour valider ta demande. Si tu ne le trouves pas, pense à vérifier tes spams.'
      )
      replace(...getProfileHookConfig('TrackEmailChange'))
    },
    onError: () =>
      showErrorSnackBar(
        'Une erreur s’est produite lors du choix de l’adresse e-mail. Réessaie plus tard.'
      ),
  })

  const onSubmit = handleSubmit(({ newEmail }) => {
    if (!params?.token || typeof params?.token !== 'string') {
      eventMonitoring.captureException(
        new Error(`Expected a string, but received ${typeof params?.token}`)
      )
      return
    }
    selectNewEmail({ newEmail, token: params.token })
  })

  return (
    <PageWithHeader
      shouldLimitWidth
      title="Modifier mon adresse e-mail"
      scrollChildren={
        <Form.MaxWidth flex={1}>
          <EmailInputController control={control} name="newEmail" label="Nouvelle adresse e-mail" />
          <Container>
            <Banner label="Tu vas recevoir un lien de confirmation sur ton adresse e-mail actuelle. Ce lien est valable 24h." />
          </Container>
          <Button
            wording="Modifier mon adresse e-mail"
            disabled={!isValid || isPending}
            onPress={onSubmit}
          />
        </Form.MaxWidth>
      }
    />
  )
}

const Container = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.l,
  marginBottom: theme.designSystem.size.spacing.xxxl,
}))
